import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Send, LogIn } from 'lucide-react';
import axios from 'axios';
import { authSelector } from '../../features/auth/slice';
import { PUBLIC_BASE } from '../../pages/public/config';
import UserAvatar from '../UserAvatar';
import ProfilePopover from '../ProfilePopover';

function getAuthorInfo(comment) {
  if (comment.client) {
    return {
      userId: comment.client.id,
      name: comment.client.nickName || comment.client.name,
      profileImage: comment.client.profileImage,
      userType: 'client',
      adminYn: comment.client.adminYn,
    };
  }
  if (comment.member) {
    return {
      userId: comment.member.id,
      name: comment.member.nickName || comment.member.name,
      profileImage: comment.member.profileImage,
      userType: 'member',
      adminYn: 'N',
    };
  }
  return { userId: null, name: comment.writer || '알 수 없음', profileImage: null, userType: 'member', adminYn: 'N' };
}

function isMyComment(me, comment) {
  if (!me) return false;
  return (
    (me.userType === 'client' && (me.id === Number(comment.clientId) || me.adminYn === 'Y')) ||
    (me.userType === 'member' && me.id === Number(comment.memberId))
  );
}

/* ── Reply / Child comment item ── */
function ChildItem({ comment, me, onDelete, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(comment.content);
  const author = getAuthorInfo(comment);

  const handleEdit = () => {
    if (!editVal.trim()) return;
    onEdit(comment.id, editVal.trim());
    setEditing(false);
  };

  return (
    <div className="ml-8 mt-2 rounded-xl border border-white/[0.06] bg-[#1a2b42]/50 px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {author.userId ? (
            <ProfilePopover user={author}>
              <UserAvatar name={author.name} profileImage={author.profileImage} userType={author.userType} adminYn={author.adminYn} size={20} />
              <span className="text-xs font-semibold text-slate-300">{author.name}</span>
            </ProfilePopover>
          ) : (
            <>
              <UserAvatar name={author.name} profileImage={author.profileImage} userType={author.userType} adminYn={author.adminYn} size={20} />
              <span className="text-xs font-semibold text-slate-300">{author.name}</span>
            </>
          )}
          <span className="text-[11px] text-slate-500">{new Date(comment.createdAt).toLocaleDateString('ko-KR')}</span>
        </div>
        {isMyComment(me, comment) && !editing && (
          <div className="flex gap-1 shrink-0">
            <button className="text-[11px] text-slate-500 hover:text-sky-400 transition-colors" onClick={() => setEditing(true)}>수정</button>
            <button className="text-[11px] text-slate-500 hover:text-rose-400 transition-colors" onClick={() => onDelete(comment.id)}>삭제</button>
          </div>
        )}
      </div>
      <div className="mt-1.5 ml-7">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              className="flex-1 px-3 py-1.5 rounded-lg border border-white/[0.08] text-sm bg-[#1a2b42] text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30"
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
            />
            <button className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold transition-colors" onClick={handleEdit}>저장</button>
            <button className="px-3 py-1.5 rounded-lg border border-white/[0.08] text-slate-400 text-xs hover:bg-white/5 transition-colors" onClick={() => { setEditing(false); setEditVal(comment.content); }}>취소</button>
          </div>
        ) : (
          <p className="text-sm text-slate-300 whitespace-pre-wrap">{comment.content}</p>
        )}
      </div>
    </div>
  );
}

/* ── Parent comment item ── */
function CommentItem({ comment, allComments, me, onDelete, onEdit, onReply, label }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyVal, setReplyVal] = useState('');
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(comment.content);
  const author = getAuthorInfo(comment);

  const children = allComments
    .filter((c) => c.parent === comment.id)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const handleEdit = () => {
    if (!editVal.trim()) return;
    onEdit(comment.id, editVal.trim());
    setEditing(false);
  };

  const handleReply = () => {
    if (!replyVal.trim()) return;
    onReply(comment.id, replyVal.trim());
    setReplyVal('');
    setShowReplyForm(false);
  };

  return (
    <div className="rounded-xl border border-white/[0.08] overflow-hidden">
      <div className="px-4 py-3 bg-[#182334]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {author.userId ? (
              <ProfilePopover user={author}>
                <UserAvatar name={author.name} profileImage={author.profileImage} userType={author.userType} adminYn={author.adminYn} size={24} />
                <span className="text-sm font-semibold text-slate-200">{author.name}</span>
              </ProfilePopover>
            ) : (
              <>
                <UserAvatar name={author.name} profileImage={author.profileImage} userType={author.userType} adminYn={author.adminYn} size={24} />
                <span className="text-sm font-semibold text-slate-200">{author.name}</span>
              </>
            )}
            <span className="text-[11px] text-slate-500">{new Date(comment.createdAt).toLocaleDateString('ko-KR')}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {me && (
              <button className="p-1 text-slate-500 hover:text-sky-400 transition-colors text-xs" onClick={() => setShowReplyForm((v) => !v)}>
                답글
              </button>
            )}
            {isMyComment(me, comment) && !editing && (
              <>
                <button className="p-1 text-slate-500 hover:text-sky-400 transition-colors text-xs" onClick={() => setEditing(true)}>수정</button>
                <button className="p-1 text-slate-500 hover:text-rose-400 transition-colors text-xs" onClick={() => onDelete(comment.id)}>삭제</button>
              </>
            )}
          </div>
        </div>
        <div className="mt-2 ml-8">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                className="flex-1 px-3 py-1.5 rounded-lg border border-white/[0.08] text-sm bg-[#1a2b42] text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={editVal}
                onChange={(e) => setEditVal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
              />
              <button className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold transition-colors" onClick={handleEdit}>저장</button>
              <button className="px-3 py-1.5 rounded-lg border border-white/[0.08] text-slate-400 text-xs hover:bg-white/5 transition-colors" onClick={() => { setEditing(false); setEditVal(comment.content); }}>취소</button>
            </div>
          ) : (
            <p className="text-sm text-slate-300 whitespace-pre-wrap">{comment.content}</p>
          )}
        </div>
      </div>

      {/* children */}
      {(children.length > 0 || showReplyForm) && (
        <div className="px-4 pb-3 bg-[#182334] space-y-0">
          {children.map((child) => (
            <ChildItem
              key={child.id}
              comment={child}
              me={me}
              onDelete={onDelete}
              onEdit={onEdit}
            />
          ))}
          {showReplyForm && (
            <div className="ml-8 mt-2 flex items-center gap-2">
              <input
                className="flex-1 px-3 py-1.5 rounded-lg border border-white/[0.08] text-sm bg-[#1a2b42] text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 transition"
                value={replyVal}
                onChange={(e) => setReplyVal(e.target.value)}
                placeholder="답글을 입력하세요"
                onKeyDown={(e) => e.key === 'Enter' && handleReply()}
              />
              <button
                className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold transition-colors disabled:opacity-40"
                disabled={!replyVal.trim()}
                onClick={handleReply}>
                등록
              </button>
              <button
                className="px-3 py-1.5 rounded-lg border border-white/[0.08] text-slate-400 text-xs hover:bg-white/5 transition-colors"
                onClick={() => { setShowReplyForm(false); setReplyVal(''); }}>
                취소
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main exported section ── */
export default function ContentCommentSection({ targetType, targetId, label = '댓글' }) {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    axios
      .get('/api/content-comment', { params: { targetType, targetId }, withCredentials: true })
      .then((res) => setComments(res.data))
      .catch(() => {});
  }, [targetType, targetId]);

  const parentComments = comments
    .filter((c) => !c.parent)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const handleSubmit = () => {
    if (!me) { setShowLoginAlert(true); return; }
    if (!input.trim()) return;
    setLoading(true);
    axios
      .post('/api/content-comment', { targetType, targetId, content: input.trim() }, { withCredentials: true })
      .then((res) => {
        setComments((prev) => [...prev, res.data]);
        setInput('');
      })
      .catch(() => alert(`${label} 등록 중 오류가 발생했습니다.`))
      .finally(() => setLoading(false));
  };

  const handleReply = (parentId, content) => {
    if (!me) { setShowLoginAlert(true); return; }
    axios
      .post('/api/content-comment', { targetType, targetId, content, parent: parentId }, { withCredentials: true })
      .then((res) => setComments((prev) => [...prev, res.data]))
      .catch(() => alert('답글 등록 중 오류가 발생했습니다.'));
  };

  const handleEdit = (id, content) => {
    axios
      .put(`/api/content-comment/${id}`, { content }, { withCredentials: true })
      .then(() => setComments((prev) => prev.map((c) => c.id === id ? { ...c, content } : c)))
      .catch(() => alert('수정 중 오류가 발생했습니다.'));
  };

  const handleDelete = (id) => {
    if (!window.confirm(`${label}을 삭제하시겠습니까?`)) return;
    axios
      .delete(`/api/content-comment/${id}`, { withCredentials: true })
      .then(() => setComments((prev) => prev.filter((c) => c.id !== id && c.parent !== id)))
      .catch(() => alert('삭제 중 오류가 발생했습니다.'));
  };

  return (
    <>
      <div className="mt-8 rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
        <div className="px-6 py-4 bg-white/[0.03] border-b border-white/[0.08] flex items-center gap-2">
          <span className="text-slate-200 text-sm font-semibold">{label}</span>
          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400">
            {parentComments.length}
          </span>
        </div>

        <div className="p-6 space-y-4">
          {/* 입력 폼 */}
          {me ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <UserAvatar name={me.nickName || me.name} profileImage={me.profileImage} userType={me.userType} adminYn={me.adminYn} size={28} />
                <span className="text-sm font-medium text-slate-200">{me.nickName || me.name}</span>
              </div>
              <textarea
                className="w-full h-24 p-3 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent resize-none placeholder-slate-500 transition"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`${label}을 입력하세요`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-600">Ctrl+Enter로 등록</span>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !input.trim()}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    loading || !input.trim()
                      ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                      : 'bg-sky-500 hover:bg-sky-600 text-white'
                  }`}>
                  <Send size={13} />
                  {loading ? '등록 중...' : '등록'}
                </button>
              </div>
            </div>
          ) : (
            <div className="cursor-pointer" onClick={() => setShowLoginAlert(true)}>
              <div className="rounded-xl border border-white/[0.08] bg-[#1a2b42]/50 p-6 flex flex-col items-center gap-2">
                <LogIn size={20} className="text-slate-500" />
                <p className="text-sm text-slate-500">로그인 후 {label}을 작성할 수 있습니다.</p>
              </div>
            </div>
          )}

          {/* 댓글 목록 */}
          {parentComments.length > 0 && (
            <div className="pt-4 border-t border-white/[0.06] space-y-3">
              {parentComments.map((c) => (
                <CommentItem
                  key={c.id}
                  comment={c}
                  allComments={comments}
                  me={me}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onReply={handleReply}
                  label={label}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 로그인 알림 모달 */}
      {showLoginAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[#1a2b42] border border-white/10 rounded-2xl p-6 max-w-sm mx-4 shadow-2xl">
            <p className="text-slate-200 text-sm text-center mb-5">
              {label}을 작성하려면 로그인이 필요합니다.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLoginAlert(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-white/[0.08] text-slate-400 text-sm font-semibold hover:bg-white/5 transition-colors">
                취소
              </button>
              <button
                onClick={() => {
                  setShowLoginAlert(false);
                  navigate(`${PUBLIC_BASE}/login`);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors">
                로그인
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
